import React from 'react';
import { FileText, Download } from 'lucide-react';
import Modal from './Modal';

/**
 * A document, opened in place.
 *
 * Every file in the app — a candidate's CV, their Right to Work scan, a
 * compliance certificate, a staff document — opens here rather than in a new
 * browser tab. A new tab loses the page the manager was working through: they
 * come back to find their place gone, and on a phone the tab is a separate
 * screen entirely. Reviewing evidence means looking at the file WHILE reading
 * the record beside it.
 *
 * Images and PDFs render inline. Anything else (a Word document, say) cannot be
 * previewed by a browser, so it says so plainly and offers the download rather
 * than showing an empty frame.
 *
 * Download is always available, because a preview is not a substitute for the
 * file — a manager printing a DBS certificate needs the actual document.
 *
 * @param {object|null} file  { url, name, title, kind? } — null closes it;
 *                             kind 'image' for files known to be photos
 */
const isImage = (value) => /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(value || '');
const isPdf = (value) => /\.pdf(\?|$)/i.test(value || '');

/**
 * Save the file without leaving the page. Files live on another origin (the
 * API / S3), where a link's `download` attribute is ignored and the browser
 * just opens yet another tab — so fetch it and save the blob. If the file
 * host won't allow that, fall back to opening it.
 */
const saveFile = async (file) => {
  try {
    const res = await fetch(file.url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const href = URL.createObjectURL(await res.blob());
    const link = document.createElement('a');
    link.href = href;
    link.download = file.name || 'document';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  } catch {
    window.open(file.url, '_blank', 'noopener');
  }
};

const DocumentViewer = ({ file, onClose }) => {
  if (!file?.url) return null;

  // The name is the better signal — a storage URL often carries no extension.
  const image = file.kind === 'image' || isImage(file.name) || isImage(file.url);
  const pdf = isPdf(file.name) || isPdf(file.url);

  return (
    <Modal
      isOpen={Boolean(file)}
      onClose={onClose}
      title={file.title || file.name || 'Document'}
      maxWidth="max-w-3xl"
      closeOnBackdrop
      footer={(
        <div className="flex items-center justify-between w-full gap-3">
          <span className="text-[12px] text-[#64748B] dark:text-gray-400 truncate">{file.name}</span>
          <a
            href={file.url}
            download={file.name || true}
            onClick={(e) => { e.preventDefault(); saveFile(file); }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 shrink-0"
          >
            <Download className="w-4 h-4" /> Download
          </a>
        </div>
      )}
    >
      <div className="flex items-center justify-center bg-[#0B1220]/5 dark:bg-black/30 rounded-xl min-h-[320px] max-h-[70vh] overflow-auto p-3">
        {image ? (
          <img src={file.url} alt={file.name || 'Document'} className="max-w-full max-h-[65vh] object-contain rounded-lg" />
        ) : pdf ? (
          <iframe src={file.url} title={file.name || 'Document'} className="w-full h-[68vh] rounded-lg bg-white" />
        ) : (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 mx-auto text-[#94A3B8] mb-3" />
            <p className="text-[13px] text-[#475569] dark:text-gray-300">Preview isn&apos;t available for this file type.</p>
            <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-1">Use the Download button to save it and open it on your computer.</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DocumentViewer;
